// src/pages/templates/TemplateEditorPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  ArrowLeft, 
  Save, 
  X, 
  FileText, 
  AlertTriangle,
  Loader2,
  CheckCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

// Import the Enhanced Template Editor
import EnhancedTemplateEditor from './components/EnhancedTemplateEditor';

// Import your API service - adjust path as needed
import { templateService } from '../../services/';

const TemplateEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditMode = !!id;
  
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Template state
  const [templateName, setTemplateName] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Fetch template for editing
  const { 
    data: template, 
    isLoading: templateLoading, 
    error: templateError 
  } = useQuery(
    ['template', id],
    () => templateService.getById(parseInt(id)),
    {
      enabled: isEditMode,
      onSuccess: (data) => {
        setTemplateName(data.Name || '');
        setTemplateContent(data.Content || '');
        setIsActive(data.IsActive !== false);
        setHasUnsavedChanges(false);
      },
      onError: (error) => {
        console.error('Error fetching template:', error);
        if (error.response?.status === 404) {
          toast.error('Template-ul nu a fost găsit');
          navigate('/templates');
        } else {
          toast.error('Eroare la încărcarea template-ului');
        }
      }
    }
  );

  // Create template mutation
  const createMutation = useMutation(
    (data) => templateService.create(data),
    {
      onSuccess: (data) => {
        toast.success('Template creat cu succes!');
        queryClient.invalidateQueries('templates');
        setHasUnsavedChanges(false);
        navigate(`/templates/${data.Id}`);
      },
      onError: (error) => {
        console.error('Error creating template:', error);
        toast.error('Eroare la crearea template-ului');
        setIsSaving(false);
      }
    }
  );

  // Update template mutation
  const updateMutation = useMutation(
    (data) => templateService.update(parseInt(id), data),
    {
      onSuccess: (data) => {
        toast.success('Template actualizat cu succes!');
        queryClient.invalidateQueries('templates');
        queryClient.invalidateQueries(['template', id]);
        setHasUnsavedChanges(false);
        navigate(`/templates/${data.Id}`);
      },
      onError: (error) => {
        console.error('Error updating template:', error);
        toast.error('Eroare la actualizarea template-ului');
        setIsSaving(false);
      }
    }
  );

  // Handle template save from EnhancedTemplateEditor
  const handleSave = async (templateData) => {
    setIsSaving(true);
    
    try {
      const dataToSave = {
        Name: templateData.name,
        Content: templateData.content,
        IsActive: templateData.isActive !== false
      };

      if (isEditMode) {
        await updateMutation.mutateAsync(dataToSave);
      } else {
        await createMutation.mutateAsync(dataToSave);
      }
    } catch (error) {
      // Error handling is done in the mutations
      console.error('Save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle template changes to track unsaved changes
  const handleTemplateChange = (name, content) => {
    const nameChanged = name !== (template?.Name || '');
    const contentChanged = content !== (template?.Content || '');
    setHasUnsavedChanges(nameChanged || contentChanged);
  };

  // Prevent navigation with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('Aveți modificări nesalvate. Sunteți sigur că doriți să ieșiți?')) {
        navigate(isEditMode ? `/templates/${id}` : '/templates');
      }
    } else {
      navigate(isEditMode ? `/templates/${id}` : '/templates');
    }
  };

  // Loading state
  if (templateLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="animate-pulse">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-gray-200 rounded"></div>
                  <div className="h-8 bg-gray-200 rounded w-64"></div>
                </div>
                <div className="flex space-x-2">
                  <div className="w-24 h-10 bg-gray-200 rounded"></div>
                  <div className="w-24 h-10 bg-gray-200 rounded"></div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="h-12 bg-gray-200 rounded"></div>
                <div className="h-96 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (templateError && isEditMode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Template Negăsit
            </h2>
            <p className="text-gray-600 mb-6">
              Template-ul solicitat nu a fost găsit sau a apărut o eroare.
            </p>
            <button
              onClick={() => navigate('/templates')}
              className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
            >
              Înapoi la Template-uri
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleCancel}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Înapoi
              </button>
              <div className="flex items-center space-x-2">
                <FileText className="w-6 h-6 text-gray-400" />
                <h1 className="text-xl font-semibold text-gray-900">
                  {isEditMode ? 'Editare Template' : 'Template Nou'}
                </h1>
              </div>
              {hasUnsavedChanges && (
                <div className="flex items-center text-amber-600">
                  <div className="w-2 h-2 bg-amber-400 rounded-full mr-2"></div>
                  <span className="text-sm font-medium">Modificări nesalvate</span>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {isSaving && (
                <div className="flex items-center text-blue-600">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <span className="text-sm">Se salvează...</span>
                </div>
              )}
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <X className="w-4 h-4 mr-2 inline" />
                Anulează
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EnhancedTemplateEditor
          templateId={isEditMode ? parseInt(id) : null}
          initialTemplate={template ? {
            name: template.Name,
            content: template.Content,
            isActive: template.IsActive
          } : null}
          onSave={handleSave}
          onCancel={handleCancel}
          onChange={handleTemplateChange}
          isSaving={isSaving}
          isEditMode={isEditMode}
        />
      </div>
    </div>
  );
};

export default TemplateEditorPage;